import styles from "./Paginator.module.scss";
import classNames from "classnames";
import { ReactNode, useMemo } from "react";
import pluralize from "pluralize";
import { useSelector } from "react-redux";
import {
  getDataset,
  getIsCardslistLoading,
  getPaginationMetadataForCluster,
  getPaginationMetadataForData,
  getTotalPagesForCluster,
  getTotalPagesForData,
} from "../../../../../../redux/SingleDataset/selectors";
import { formatNumberWithCommas } from "../../../../../../helpers/utility/formatters";
import { NextIcon, PrevIcon, VideoIcon } from "./Icons";
import { useSearchParams } from "react-router-dom";
import {
  PAGE_PARAMETER,
  REVIEW_PARAMETER,
  SEARCH_TEXT_PARAMETER,
  VQL_PARAMETER,
} from "../../../../../../helpers/constants/miscellaneous";
import { useParams } from "react-router";
import { useEntityType } from "../../../../../../hooks/useEntityType";
import { Dataset, EntityType } from "../../../../../../types";
import useAllQueryParamsFilters from "../../../../../../hooks/useAllQueryParamsFilters";
import ObjectIcon from "../../../../../../assets/icons/ObjectIcon";
import ImageIcon from "../../../../../../assets/icons/imageIcon";
import useVqlParams from "../../../../../../hooks/useVqlParams";
import { useFeatureFlagService } from "../../../../../../hooks/useFeatureFlagService";
import { VQL_UPGRADE } from "../../../../../../helpers/constants/filters";
import { RelevanceSlider } from "../RelevanceSlider/RelevanceSlider";

export const Paginator = ({
  items,
  className,
  reviewCounters,
  isFlatResponse,
  inReview,
}: {
  isFlatResponse: boolean;
  reviewCounters?: ReactNode;
  className?: string;
  items: any[];
  inReview: boolean;
}) => {
  const dataset = useSelector(getDataset);
  const isCardListLoading = useSelector(getIsCardslistLoading);
  return (
    <div className={classNames(styles.paginator, className)}>
      {inReview ? (
        reviewCounters
      ) : (
        <PageData
          dataset={dataset}
          items={items || []}
          isFlatResponse={isFlatResponse}
          isCardListLoading={isCardListLoading}
        />
      )}
    </div>
  );
};

const PageData = ({
  items,
  isFlatResponse,
  isCardListLoading,
  dataset,
}: {
  dataset: Dataset | undefined;
  isCardListLoading: boolean;
  isFlatResponse: boolean;
  items: any[];
}) => {
  const { clusterId } = useParams();
  const { enabled: isVqlUpgradeEnabled } = useFeatureFlagService(VQL_UPGRADE);
  const queryParamsFilters = useAllQueryParamsFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  const totalPagesForData = useSelector(getTotalPagesForData);
  const totalPagesForCluster = useSelector(getTotalPagesForCluster);
  const isFilteringOrSearching =
    queryParamsFilters[VQL_PARAMETER] ||
    queryParamsFilters[SEARCH_TEXT_PARAMETER];
  const entityType = useEntityType();
  const page = Number(searchParams.get(PAGE_PARAMETER) || 1);
  const totalPages = clusterId ? totalPagesForCluster : totalPagesForData;
  const inReview = searchParams.has(REVIEW_PARAMETER);
  const { vqlQueries } = useVqlParams();

  const showRelevanceSlider = useMemo(() => {
    return (
      isVqlUpgradeEnabled &&
      vqlQueries.some(
        (q) =>
          ("text" in q && q.text?.op === "semantic") ||
          ("similarity" in q && q.similarity),
      )
    );
  }, [vqlQueries, isVqlUpgradeEnabled]);

  const handlePrevClick = () => {
    if (page > 1) {
      searchParams.set(PAGE_PARAMETER, String(page - 1));
      setSearchParams(searchParams);
    }
  };
  const handleNextClick = () => {
    if (page < totalPages) {
      searchParams.set(PAGE_PARAMETER, String(page + 1));
      setSearchParams(searchParams);
    }
  };
  return (
    <div className={styles.wrapper}>
      <div className={styles.datasetCounters}>
        {dataset && (
          <DatasetCounters
            isFilteringOrSearching={Boolean(isFilteringOrSearching)}
            dataset={dataset}
            entityType={entityType}
          />
        )}
      </div>
      {showRelevanceSlider && <RelevanceSlider className={styles.slider} />}
      <div className={styles.filler} />
      {isCardListLoading ? (
        !inReview && <div className={styles.loader} />
      ) : (
        <div className={styles.summary}>
          <PageCounters
            entityType={entityType}
            page={page}
            clusterId={clusterId}
            items={items}
            isFlatResponse={isFlatResponse}
          />
          <div className={styles.buttons}>
            <PrevIcon
              onClick={handlePrevClick}
              className={classNames(styles.button, {
                [styles.disabled]: page === 1,
              })}
            />
            <NextIcon
              onClick={handleNextClick}
              className={classNames(styles.button, {
                [styles.disabled]: page === totalPages,
              })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const DatasetCounters = ({
  dataset,
  entityType,
  isFilteringOrSearching,
}: {
  isFilteringOrSearching: boolean;
  entityType: EntityType;
  dataset: Dataset;
}) => {
  return (
    <>
      <>{isFilteringOrSearching ? "Top search results from " : "Total "}</>
      {entityType === "OBJECTS" ? (
        <>
          <ObjectIcon /> {formatNumberWithCommas(dataset.n_objects)} objects
        </>
      ) : (
        <>
          {Boolean(dataset.n_videos) && (
            <>
              <VideoIcon />
              {` ${formatNumberWithCommas(dataset.n_videos)} videos `}
            </>
          )}
          {Boolean(dataset.n_images) && (
            <>
              <ImageIcon /> {formatNumberWithCommas(dataset.n_images)} images
            </>
          )}
        </>
      )}
    </>
  );
};

const PageCounters = ({
  clusterId,
  page,
  items,
  isFlatResponse,
  entityType,
}: {
  entityType: EntityType;
  items: any[];
  isFlatResponse: boolean;
  clusterId?: string;
  page: number;
}) => {
  const metadataForData = useSelector(getPaginationMetadataForData);
  const metadataForCluster = useSelector(getPaginationMetadataForCluster);
  const totalItems = clusterId
    ? metadataForCluster?.total
    : metadataForData?.total;
  const pageSize = clusterId ? metadataForCluster?.size : metadataForData?.size;
  const start = `${(page - 1) * pageSize + (totalItems ? 1 : 0)}`;
  const end = Math.min(totalItems, page * pageSize);
  const { framesCount, imagesCount, objectsCount } = useMemo(() => {
    const allItems = items
      .map((item) => (isFlatResponse && !clusterId ? item.previews : item))
      .flat();
    const getCount = (value: number) =>
      isFlatResponse || clusterId ? 1 : value;
    return allItems.reduce(
      (out, item) => {
        if (!item) {
          return out;
        }
        if (entityType === "OBJECTS") {
          out.objectsCount += getCount(item.n_objects);
        } else if (item.n_frames > 0 || item.video_uri) {
          out.framesCount += getCount(item.n_frames);
        } else {
          out.imagesCount += getCount(item.n_images);
        }
        return out;
      },
      { framesCount: 0, imagesCount: 0, objectsCount: 0 },
    );
  }, [isFlatResponse, items, clusterId, entityType]);
  return (
    <div>
      {start} - {end} {!isFlatResponse && !clusterId && "Clusters "}(
      {totalItems &&
        [
          [objectsCount, "Object"],
          [framesCount, "Frame"],
          [imagesCount, "Image"],
        ]
          .map(([count, text]) =>
            count > 0
              ? `${formatNumberWithCommas(count)} ${pluralize(text, count)}`
              : "",
          )
          .filter(Boolean)
          .join(", ")}
      )
    </div>
  );
};
